import * as Sentry from "@sentry/react";
import * as React from "react";

import {
  useCompletedFormState,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { TRACTOR_DETAILS_STEP, TRACTOR_INTRO_STEP } from "..";
import {
  TractorPriceRequestInfo,
  TractorProductDetailsFormValues,
  TractorProductIntroFormValues,
  TractorTexts,
} from "../tractorInterface";

import { PaddedSurface as Surface } from "App/global-style";
import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { TextLanguage } from "Store/texts/interfaces";
import { mapValidationErrors } from "Utils/formUtils";
import { isDefined } from "Utils/functions";
import { mapFormToPriceRequest } from "../tractorUtils";
import { FormElements } from "./FormElements";
import { validationSchema } from "./tractorProductDetailsValidation";

interface Props {
  readonly sendPriceRequest: (payload: TractorPriceRequestInfo) => void;
  readonly texts: TractorTexts;
  readonly lang: TextLanguage;
}

export const TractorProductDetails = (props: Props): JSX.Element => {
  const { sendPriceRequest, texts, lang } = props;
  const dispatch = useDispatch();
  const introFormValues = useCompletedFormState<TractorProductIntroFormValues>(
    TRACTOR_INTRO_STEP.id,
  );
  const initialFormState = useFormState<TractorProductDetailsFormValues>(
    TRACTOR_DETAILS_STEP.id,
    initialFormValues,
  );

  const { isLoading, error } = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );

  const submitHandler = (formValues: TractorProductDetailsFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: TRACTOR_DETAILS_STEP.id,
        formState: formValues,
      }),
    );
    sendPriceRequest(mapFormToPriceRequest(introFormValues, formValues));
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductDetails - ${INSURANCE_NAME_VALUE.TRACTOR}`,
        );
      }}
    >
      <ProductDetailsTile
        header={texts.tile.productDetails.heading[lang]}
        initialValues={initialFormState}
        validationSchema={validationSchema()}
        onSubmit={submitHandler}
      >
        {(formProps: FormikProps<TractorProductDetailsFormValues>) => (
          <Form>
            <Surface>
              <FormElements
                formProps={formProps}
                texts={texts.formText.checkPrice}
                lang={lang}
              />

              {isDefined(error) && (
                <SystemErrorAlert alertId="priceCalculation" />
              )}

              <ErrorsSummedUp
                errors={mapValidationErrors(
                  formProps.errors,
                  texts.details,
                  lang,
                )}
                isVisible
              />

              <ScrollToFirstError />
            </Surface>
            <NavigationButtons isLoading={isLoading} />
          </Form>
        )}
      </ProductDetailsTile>
    </Sentry.ErrorBoundary>
  );
};

const initialFormValues = {
  coverageAmount: undefined,
  injuries: undefined,
  injuryYear: undefined,
};
